import type { AccordionConfig } from "@ig_accordion/Sources/TypeScript/accordion.types.js";

declare global {
    interface Window {
        accordionHandler: AccordionDetailsHandler;
    }
}

class AccordionDetailsHandler {
    private readonly config: AccordionConfig;
    //    private readonly htmlTag: HTMLHtmlElement | null;
    private readonly urlHash: string;
    private readonly selectorClass: string;
    private readonly elements: NodeListOf<HTMLDetailsElement>;
    private readonly scrollOffset: number;
    private readonly breakpoint: number;

    constructor(selectorClass: string, breakpoint: number, config: AccordionConfig) {
        this.config = config;
        //	this.htmlTag = document.querySelector("html");
        this.urlHash = window.location.hash;
        this.selectorClass = selectorClass;
        this.elements = document.querySelectorAll<HTMLDetailsElement>(`details.${selectorClass}`);
        this.scrollOffset = this.config.scrollOffset;
        this.breakpoint = breakpoint;
        this.init();
    }

    private init(): void {
        this.resolveNestedNaming();
        const matchedElement = this.setExpanded();
        this.afterDomUpdate(() => {
            this.addElementListener();
            if (matchedElement && this.config.enableScrolling) {
                this.scrollTo(matchedElement);
            }
        });
    }

    // Waits two animation frames to ensure the DOM has fully updated
    private afterDomUpdate(callback: () => void): void {
        requestAnimationFrame(() => requestAnimationFrame(callback));
    }

    private resolveNestedNaming(): void {
        let groupCounter = 0;

        this.elements.forEach((element) => {
            const nestedChildren = element.querySelectorAll<HTMLDetailsElement>(`details.${this.selectorClass}`);
            if (nestedChildren.length > 0) {
                groupCounter++;
                // Isolate nested children into their own private structural group name
                nestedChildren.forEach((detailsChild) => {
                    if (detailsChild.hasAttribute("name")) {
                        detailsChild.setAttribute("name", `accordion-subgroup-${groupCounter}`);
                    }
                });
            }
        });
    }

    private setExpanded(): HTMLDetailsElement | null {
        const isResp = this.isResponsive();
        const currentHash = this.urlHash.replace("#", "");
        let matchedElement: HTMLDetailsElement | null = null;

        if (currentHash) {
            this.elements.forEach((element) => {
                if (element.dataset.hashId === currentHash) {
                    element.open = true;
                    matchedElement = element;
                    this.openParent(element);
                }
            });
        }

        this.elements.forEach((element) => {
            if (!matchedElement) {
                const shouldOpenDesktop = element.classList.contains("desktop1") && !isResp;
                const shouldOpenMobile = element.classList.contains("mobile1") && isResp;

                if (shouldOpenDesktop || shouldOpenMobile) {
                    element.open = true;
                }
            }
            element.classList.remove("desktop1", "desktop0", "mobile1", "mobile0");
        });

        return matchedElement;
    }

    private openParent(element: HTMLDetailsElement): void {
        let current = element;
        while (true) {
            const parent = current.parentElement?.closest<HTMLDetailsElement>(`details.${this.selectorClass}`);
            if (!parent) break;
            parent.open = true;
            current = parent;
        }
    }

    private addElementListener(): void {
        this.elements.forEach((element) => {
            element.addEventListener("toggle", () => {
                if (element.open) {
                    this.handleOpen(element);
                } else {
                    this.handleClose(element);
                }
            });
        });
    }

    private handleOpen(element: HTMLDetailsElement): void {
        const globalEnabled = !!this.config.enableScrolling;
        const localForced = element.dataset?.enableAccordionScrolling === "1" || element.dataset?.enableAccordionScrolling === "true";
        if (globalEnabled || localForced) this.scrollTo(element);
        const hashId = element.dataset.hashId ?? "";
        if (!hashId) return;
        if (window.location.hash !== `#${hashId}`) {
            history.replaceState(null, "", `#${hashId}`);
        }
    }

    private handleClose(element: HTMLDetailsElement): void {
        const hashId = element.dataset.hashId ?? "";
        if (hashId && window.location.hash === `#${hashId}`) {
            history.replaceState(null, "", window.location.pathname + window.location.search);
        }
        element.querySelectorAll<HTMLDetailsElement>(`details.${this.selectorClass}[open]`).forEach((el) => (el.open = false));
    }

    private scrollTo(element: HTMLDetailsElement): void {
        this.applyScrollMargin(element);
        element.scrollIntoView({ behavior: "smooth" });
    }

    private applyScrollMargin(element: HTMLDetailsElement): void {
        const margin = this.getScrollMargin();
        if (margin) {
            element.style.scrollMarginTop = `${margin}px`;
        }
    }

    private getScrollMargin(): number {
        const header: HTMLElement | null = document.querySelector(this.config.scrollHeaderElement || "#header");
        if (header && getComputedStyle(header).position === "fixed") {
            return this.outerHeight(header) + this.scrollOffset;
        }
        return this.scrollOffset;
    }

    private outerHeight(element: HTMLElement | null, includeMargins = false): number {
        if (!element) return 0;

        let height = element.offsetHeight;

        if (includeMargins) {
            const style = getComputedStyle(element);
            height += ["marginTop", "marginBottom"].reduce((sum, prop) => sum + (parseFloat(style[prop as any]) || 0), 0);
        }
        return height;
    }

    private isResponsive(): boolean {
        return this.breakpoint > window.innerWidth;
    }
}

export { AccordionDetailsHandler };
